"use client";

import React, { useState } from "react";
import {
  Box,
  Button,
  Text,
  VStack,
  useToast,
  IconButton,
  HStack,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Badge,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from "@chakra-ui/react";
import { ChevronDownIcon, DeleteIcon } from "@chakra-ui/icons";
import { Copy, Mail, LinkIcon } from "lucide-react";

import type { EmailAddress } from "../../email-services/emailAddressService";
import { emailAddressService } from "../../email-services/emailAddressService";

type SendEmailIconProps = {
  size?: number;
  color?: string;
  strokeWidth?: number;
  background?: string;
  opacity?: number;
  rotation?: number;
  shadow?: number;
  flipHorizontal?: boolean;
  flipVertical?: boolean;
  padding?: number;
};

const SendEmailIcon: React.FC<SendEmailIconProps> = ({
  size = 16,
  color = "currentColor",
  strokeWidth = 2,
  background = "transparent",
  opacity = 1,
  rotation = 0,
  shadow = 0,
  flipHorizontal = false,
  flipVertical = false,
  padding = 0,
}) => {
  const transforms: string[] = [];
  if (rotation !== 0) transforms.push(`rotate(${rotation}deg)`);
  if (flipHorizontal) transforms.push("scaleX(-1)");
  if (flipVertical) transforms.push("scaleY(-1)");

  // Your path coordinates go up to ~44, so use a 48x48 coordinate system.
  const base = 48;
  const viewBoxSize = base + padding * 2;
  const viewBoxOffset = -padding;
  const viewBox = `${viewBoxOffset} ${viewBoxOffset} ${viewBoxSize} ${viewBoxSize}`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      width={size}
      height={size}
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        opacity,
        transform: transforms.join(" ") || undefined,
        filter:
          shadow > 0
            ? `drop-shadow(0 ${shadow}px ${shadow * 2}px rgba(0,0,0,0.3))`
            : undefined,
        backgroundColor: background !== "transparent" ? background : undefined,
      }}
    >
      <g fill="none">
        <path d="M44 24V9H24H4V24V39H24" />
        <path d="M44 34L30 34" />
        <path d="M39 29L44 34L39 39" />
        <path d="M4 9L24 24L44 9" />
      </g>
    </svg>
  );
};

interface EmailAddressDetailsProps {
  emailAddress: EmailAddress;
  agents: Array<{ id: string; name: string }>;
  onUpdate: () => void;
  onDelete: () => void;
  onSendEmail: () => void;
}

interface CustomSelectProps {
  label: string;
  value: string;
  placeholder: string;
  options: Array<{ value: string; label: string }>;
  onSelect: (value: string) => void;
  isDisabled: boolean;
  cardBg: string;
  cardComponentsBg: string;
  textColor: string;
  borderColor: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  label,
  value,
  placeholder,
  options,
  onSelect,
  isDisabled,
  cardBg,
  cardComponentsBg,
  textColor,
  borderColor,
}) => {
  const selectedOption = options.find((opt) => opt.value === value);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  return (
    <Box>
      <Text mb={2} fontWeight="medium" color={textColor}>
        {label}
      </Text>
      <Menu>
        <MenuButton
          as={Button}
          ref={buttonRef}
          rightIcon={<ChevronDownIcon />}
          isDisabled={isDisabled}
          bg={cardBg}
          color={textColor}
          borderColor={borderColor}
          borderWidth="1px"
          _hover={{ bg: cardBg }}
          _expanded={{ bg: cardBg }}
          w="100%"
          textAlign="left"
        >
          {selectedOption ? selectedOption.label : placeholder}
        </MenuButton>

        <MenuList
          bg={cardBg}
          borderColor={borderColor}
          minW={
            buttonRef.current ? `${buttonRef.current.offsetWidth}px` : "100%"
          }
        >
          <MenuItem
            onClick={() => onSelect("")}
            _hover={{ bg: "gray.300" }}
            bg={cardComponentsBg}
            color={textColor}
          >
            {placeholder}
          </MenuItem>

          {options.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => onSelect(option.value)}
              _hover={{ bg: "gray.300" }}
              bg={cardComponentsBg}
              color={textColor}
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  );
};

export const EmailAddressDetails: React.FC<EmailAddressDetailsProps> = ({
  emailAddress,
  agents,
  onUpdate,
  onDelete,
  onSendEmail,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const toast = useToast();

  const cardBg = "transparent";
  const textColor = "gray.900";
  const cardComponentsBg = "white";
  const borderColor = "gray.500";

  const handleUpdate = async (
    field: string,
    value: string | number | boolean | null
  ) => {
    setIsUpdating(true);
    try {
      await emailAddressService.update(emailAddress.id, { [field]: value });
      onUpdate();
      toast({
        title: "Email address updated",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Error updating email address",
        description:
          error instanceof Error ? error.message : "An error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const agentOptions = agents.map((agent) => ({
    value: agent.id,
    label: agent.name,
  }));

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(emailAddress.email_address);
      toast({
        title: "Email address copied",
        status: "success",
        duration: 2000,
      });
    } catch {
      toast({
        title: "Could not copy",
        description: "Clipboard permissions blocked in this browser context.",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await emailAddressService.delete(emailAddress.id);
      onDelete();
      toast({
        title: "Email address deleted",
        status: "success",
        duration: 3000,
      });
      onClose();
    } catch (error) {
      toast({
        title: "Error deleting email address",
        description:
          error instanceof Error ? error.message : "An error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (date: string | null | undefined) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString();
  };

  const canSendEmail = Boolean(
    emailAddress.is_connected && emailAddress.assigned_agent_id
  );

  return (
    <Box p={6} bg={cardBg} borderRadius="md">
      <VStack align="stretch" spacing={6}>
        <Box>
          <HStack justifyContent="space-between" alignItems="flex-start" mb={4}>
            <VStack align="start" spacing={2}>
              <HStack spacing={3}>
                <Mail size={24} color="#4285F4" />
                <Text fontSize="xl" fontWeight="bold" color={textColor}>
                  {emailAddress.email_address}
                </Text>
              </HStack>

              <HStack spacing={2}>
                <Badge
                  colorScheme={emailAddress.is_connected ? "green" : "red"}
                  fontSize="sm"
                >
                  {emailAddress.is_connected ? "Connected" : "Disconnected"}
                </Badge>

                <Badge colorScheme="blue" fontSize="sm">
                  <HStack spacing={1}>
                    <LinkIcon size={12} />
                    <Text>{emailAddress.oauth_provider || "Gmail"}</Text>
                  </HStack>
                </Badge>

                <IconButton
                  aria-label="Copy email address"
                  icon={<Copy size={14} />}
                  onClick={handleCopyEmail}
                  size="xs"
                  variant="ghost"
                  color="gray.500"
                  _hover={{ bg: "gray.200" }}
                />
              </HStack>
            </VStack>

            <HStack spacing={2}>
              <IconButton
                aria-label="Send initial email"
                icon={<SendEmailIcon size={16} />}
                onClick={onSendEmail}
                variant="outline"
                colorScheme="green"
                color="green.500"
                _hover={{ bg: "gray.200" }}
                size="sm"
                isDisabled={!canSendEmail}
              />

              <IconButton
                aria-label="Delete email address"
                icon={<DeleteIcon />}
                onClick={onOpen}
                variant="outline"
                colorScheme="red"
                color="red.500"
                _hover={{ bg: "gray.200" }}
                size="sm"
              />
            </HStack>
          </HStack>
        </Box>

        <Divider />

        <Box>
          <Text fontSize="lg" fontWeight="semibold" color={textColor} mb={4}>
            Connection Information
          </Text>

          <VStack align="stretch" spacing={4}>
            <HStack spacing={6}>
              <Stat>
                <StatLabel color="gray.600">Status</StatLabel>
                <StatNumber fontSize="sm">
                  <Badge
                    colorScheme={emailAddress.is_active ? "green" : "gray"}
                  >
                    {emailAddress.is_active ? "Active" : "Inactive"}
                  </Badge>
                </StatNumber>
              </Stat>

              <Stat>
                <StatLabel color="gray.600">Provider</StatLabel>
                <StatNumber fontSize="sm" color={textColor}>
                  {emailAddress.oauth_provider || "Gmail"}
                </StatNumber>
              </Stat>
            </HStack>

            <HStack spacing={6}>
              <Stat>
                <StatLabel color="gray.600">Connected At</StatLabel>
                <StatNumber fontSize="xs" color={textColor}>
                  {formatDate(emailAddress.connected_at)}
                </StatNumber>
              </Stat>

              <Stat>
                <StatLabel color="gray.600">Last Synced</StatLabel>
                <StatNumber fontSize="xs" color={textColor}>
                  {formatDate(emailAddress.last_sync_at)}
                </StatNumber>
              </Stat>
            </HStack>

            {emailAddress.watch_expiration && (
              <Stat>
                <StatLabel color="gray.600">Watch Expiration</StatLabel>
                <StatNumber fontSize="xs" color={textColor}>
                  {formatDate(emailAddress.watch_expiration)}
                </StatNumber>
                <StatHelpText color="gray.500">
                  Gmail push notifications active until this time
                </StatHelpText>
              </Stat>
            )}

            {emailAddress.last_error && (
              <Box
                p={3}
                bg="red.50"
                borderRadius="md"
                borderWidth="1px"
                borderColor="red.200"
              >
                <Text fontSize="sm" fontWeight="medium" color="red.800" mb={1}>
                  Last Error
                </Text>
                <Text fontSize="xs" color="red.600">
                  {emailAddress.last_error}
                </Text>
              </Box>
            )}
          </VStack>
        </Box>

        <Divider />

        <CustomSelect
          label="Assigned Agent"
          value={emailAddress.assigned_agent_id || ""}
          placeholder="None (unassigned)"
          onSelect={(val) => handleUpdate("assigned_agent_id", val || null)}
          isDisabled={isUpdating}
          cardBg={cardBg}
          cardComponentsBg={cardComponentsBg}
          textColor={textColor}
          borderColor={borderColor}
          options={agentOptions}
        />
      </VStack>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={cardComponentsBg}>
            <AlertDialogHeader color={textColor}>
              Delete Email Address
            </AlertDialogHeader>

            <AlertDialogBody color={textColor}>
              Are you sure you want to delete this email address? This will
              disconnect your Gmail account and remove all associated data. This
              action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                colorScheme="white"
                textColor="gray.800"
                _hover={{ bg: "gray.300" }}
                variant="ghost"
                ref={cancelRef}
                onClick={onClose}
              >
                Cancel
              </Button>

              <Button
                colorScheme="red"
                onClick={handleDelete}
                ml={3}
                isLoading={isDeleting}
                background="red.300"
                _hover={{ bg: "red.500" }}
                textColor="gray.800"
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};
